/**
 * Debug Utilities for FirstDistro SDK
 * 
 * Shared utilities for debug badge and overlay.
 * All methods are SSR-safe.
 */

const STORAGE_KEY_DISMISSED = 'fd_debug_dismissed';

/**
 * Check if debug mode is active (via URL parameter)
 */
export function isDebugMode(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('fd_debug') === 'true';
  } catch {
    return false;
  }
}

/**
 * Check if debug was dismissed by user
 */
export function isDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    return localStorage.getItem(STORAGE_KEY_DISMISSED) === 'true';
  } catch {
    // localStorage may be blocked
    return false;
  }
}

/**
 * Mark debug as dismissed (remember in localStorage)
 */
export function dismiss(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(STORAGE_KEY_DISMISSED, 'true');
  } catch {
    // localStorage may be blocked
  }
}

/**
 * Clear dismissed state (for testing or re-enabling)
 */
export function clearDismissed(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(STORAGE_KEY_DISMISSED);
  } catch {
    // localStorage may be blocked
  }
}

export type DebugStatus = 'connected' | 'error' | 'loading';

/**
 * SDK instance interface for debug utilities
 * Uses minimal interface to avoid tight coupling
 */
export interface DebugSDKInstance {
  userId?: string;
  userTraits?: Record<string, any>;
  accountId?: string;
  accountTraits?: Record<string, any>;
  eventTracker?: unknown;
  currentConfig?: unknown;
  configManager?: {
    getVendorId(): string;
    getApiUrl(): string;
  };
}

/**
 * Get SDK status for display
 */
export function getStatus(instance: DebugSDKInstance | null | undefined): DebugStatus {
  if (!instance) return 'loading';
  
  // Check if config is loaded and tracking is available
  if (instance.currentConfig && instance.eventTracker) {
    return 'connected';
  }
  
  // Config loaded but no tracker (tracking disabled)
  if (instance.currentConfig) {
    return 'connected';
  }
  
  return 'loading';
}

/**
 * Get status icon for display
 */
export function getStatusIcon(status: DebugStatus): string {
  switch (status) {
    case 'connected':
      return '✅';
    case 'error':
      return '⚠️';
    case 'loading':
      return '⏳';
    default:
      return '❓';
  }
}

/**
 * Get status text for display
 */
export function getStatusText(status: DebugStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'error':
      return 'Error';
    case 'loading':
      return 'Loading';
    default:
      return 'Unknown';
  }
}

/**
 * Get user context for display
 */
export function getUserContext(instance: DebugSDKInstance | null | undefined): string {
  if (!instance) return 'Anonymous';
  
  // Try to get email from traits
  const email = instance.userTraits?.email;
  if (email && typeof email === 'string') {
    return email;
  }
  
  // Try to get name from traits
  const name = instance.userTraits?.name;
  if (name && typeof name === 'string') {
    return name;
  }
  
  // Fall back to user ID
  if (instance.userId) {
    return instance.userId;
  }
  
  return 'Anonymous';
}

/**
 * Get account context for display
 */
export function getAccountContext(instance: DebugSDKInstance | null | undefined): string {
  if (!instance) return 'Not set';
  
  // Try to get name from traits
  const name = instance.accountTraits?.name;
  if (name && typeof name === 'string') {
    return name;
  }
  
  // Fall back to account ID
  if (instance.accountId) {
    return instance.accountId;
  }
  
  return 'Not set';
}

/**
 * Get dashboard URL for the vendor
 */
export function getDashboardUrl(instance: DebugSDKInstance | null | undefined): string {
  if (!instance?.configManager) {
    return 'https://firstdistro.com/dashboard/events';
  }
  
  try {
    const apiUrl = instance.configManager.getApiUrl();
    // Use the API URL base for dashboard
    return `${apiUrl}/dashboard/events`;
  } catch {
    return 'https://firstdistro.com/dashboard/events';
  }
}

/**
 * DebugUtils class for grouped access
 */
export const DebugUtils = {
  isDebugMode,
  isDismissed,
  dismiss,
  clearDismissed,
  getStatus,
  getStatusIcon,
  getStatusText,
  getUserContext,
  getAccountContext,
  getDashboardUrl,
};
